<template>
  <div>
    <!-- Error Messages -->
    <app-errors></app-errors>

    <!-- Loading Progress Icon -->
    <app-loader></app-loader>

    <!-- Toast Messages -->
    <v-snackbar
      :timeout="timeout"
      :top="y === 'top'"
      :bottom="y === 'bottom'"
      :right="x === 'right'"
      :left="x === 'left'"
      :multi-line="mode === 'multi-line'"
      :vertical="mode === 'vertical'"
      v-model="snackbar"
    >
      {{ snackbarText }}
    </v-snackbar>

    <v-content>
      <v-card>
        <!-- Add button -->
        <v-btn
          fab
          depressed
          small
          bottom
          :right="!currentRTL"
          :left="currentRTL"
          fixed
          class="primary"
          @click.native="addItem"
          style="margin: 50px 40px"
          v-if="canAdd"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>

        <!-- Main toolbar -->
        <v-app-bar
          dark
          flat
          fixed
          app
          hide-on-scroll
          :color="headerColor"
          v-if="!searchMode"
        >
          <v-toolbar-title style="text-decoration: underline"
            >AWE</v-toolbar-title
          >

          <v-spacer></v-spacer>

          <v-btn icon @click.prevent="searchMode = 'true'">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </v-app-bar>

        <!-- Search Mode toolbar -->
        <v-app-bar
          dark
          flat
          fixed
          app
          hide-on-scroll
          :class="{ primary: dark }"
          v-if="searchMode"
        >
          <v-btn icon @click="closeSearch">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

          <v-text-field
            class="mx-3 searchbox"
            flat
            :label="$t('label.search')"
            solo-inverted
            v-model="searchString"
            clearable
            @click:clear="clearSearch"
          >
          </v-text-field>
        </v-app-bar>

        <!-- list of data -->
        <v-list two-line color="#E7E7E7">
          <div class="list-wrapper">
            <h1 class="list-header">Tasks for this session</h1>
            <template v-for="(item, index) in filteredItems">
              <v-list-item
                class="list-item"
                v-touch="{
                  left: () => swipe(item, 'Left'),
                  right: () => swipe(item, 'Right'),
                }"
                ripple
                :key="item.taskId"
                :id="item.taskId"
                @click.prevent="editItem(item)"
              >
                <!-- Show Weld Inspection Status as icon -->
                <div class="icon-wrapper">
                <v-list-item-avatar rounded="false">
                  <v-icon large v-show="item.materialChecked === 1" color="#44D7B6"
                    >mdi-checkbox-blank-circle</v-icon
                  >
                  <v-icon large v-show="item.materialChecked === -1" color="#F7B500"
                    >mdi-checkbox-blank-circle</v-icon
                  >
                  <v-icon large v-show="item.materialChecked === 0" color="red"
                    >mdi-checkbox-blank-circle</v-icon
                  >
                </v-list-item-avatar>
                </div>

                <v-list-item-content class="list-content">
                  <v-list-item-subtitle class="line4" v-if="item.scheduleDate">{{
                    moment(item.scheduleDate.toString()).format("DD MMM YYYY")
                  }}</v-list-item-subtitle>
                  <v-list-item-subtitle class="line2">{{
                    item.taskRef
                  }}</v-list-item-subtitle>
                  <v-list-item-subtitle class="line3">{{
                    item.description
                  }}</v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action @click.stop>
                  <span>
                    <!-- if complete, show as an icon -->
                    <v-icon v-if="item.isComplete == 1" small>
                      mdi-star
                    </v-icon>

                    <!-- if images exist, show as an icon -->
                    <v-icon v-if="item.has_images == 1" small>
                      mdi-camera
                    </v-icon>
                  </span>
                </v-list-item-action>
              </v-list-item>
              <v-divider
                class="no-rule"
                v-if="index + 1 > items.length"
                :key="index * -1"
              ></v-divider>
            </template>
          </div>
        </v-list>
      </v-card>
    </v-content>

    <!-- Footer -->
    <app-footer></app-footer>
  </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";

export default {
  data() {
    return {
      moment: moment,

      winTitle: "Tasks",
      pagename: "Tasks",

      snackbar: false,
      y: "top",
      x: null,
      mode: "",
      timeout: 4000,
      snackbarText: "",

      searchMode: false,
      searchString: "",
      deleteDialog: false,

      items: [],

      itemToDelete: {},
      itemToDeleteIndex: -1,
      itemToRevise: {},
      itemToReviseIndex: -1,

      canAdd: false,
    };
  },

  computed: {
    filteredItems() {
      // return this.items

      return _.orderBy(
        this.items.filter((item) => {
          // All active latest ones
          if (!this.searchString) return true;

          // All active latest ones that match search criteria
          return item.TaskRef
            .toLowerCase()
            .includes(this.searchString.toLowerCase());
        }),
        ["TaskRef"],
        ["desc"]
      );
    },

    dark() {
      if (localStorage.getItem("darkTheme") === "true") return true;
      return false;
    },

    primaryColor() {
      return localStorage.getItem("primaryColor");
    },

    headerColor() {
      if (this.dark) return "#212121";
      return "#4B4B4B";
    },

    currentLocale() {
      return localStorage.getItem("currentLocale");
    },

    currentRTL() {
      const rtl = localStorage.getItem("currentRTL");
      if (rtl === "true") return true;
      return false;
    },
  },

  methods: {
    clearSearch() {
      this.searchString = "";
    },

    closeSearch() {
      this.searchMode = false;
      this.searchString = "";
    },

    editItem(item) {
      // Store current list position so we can return to it
      this.storeListPosition(item);

      // Go to BOM page
      this.$router.push({ name: "BOM", params: { taskid: item.taskId } });
    },
    storeListPosition(item) {
      this.$store.dispatch("setListPosition", {
        pagekey: this.pagename,
        listpos: item.taskid,
      });
    },

    swipe(item, direction) {
      // May use this in future to complete welds (is_complete),
      // with a toast to undo completion
      console.log(item.TaskRef);
      console.log(direction);
    },
  },

  created() {
    let url = "";
    let payload = {};

    // Set Page Title
    this.winTitle = this.$i18n.t("title." + this.pagename);

    // Retrieve Main Data
    url = "/query/TaskBOM";
    payload = {};
    this.$store
      .dispatch("apiPost", { url, payload })
      .then((response) => {
        this.items = response.data;
      })
      .catch(() => {
        // console.log(error)
      });

    // retrieve any existing list position and scroll to that position
    // this.$store.dispatch('getListPosition', this.pagename )
    // .then ( (pos) => {
    //   if (pos) {
    //     this.$vuetify.goTo(pos, {duration: 0, offset: 5})
    //   }
    // })
  },
};
</script>
